import _ from '@sparta.global.vendors/lodash';

/**
 * Draggable Utility
 * @description Allows to drag elements within boundaries
 * @author Yelena Rose (zkymyru)
 * @copyright 2019-5-10
 * @file
 */

const DEFAULT = {
  // disallow drag on not filterTarget
  filterTarget: null,
  // limit the drag bounds
  // x: [minimum position, maximum position] || position
  // y: [minimum position, maximum position] || position
  limit: {
    x: null,
    y: null
  },
  // threshold to move before drag begins (in px)
  threshold: 0,
  // set position within provided limit
  isSetPosition: true,
  // set dimensions
  isSetDimensions: true,
  // disable dragging in a specific viewport
  disableOnSmall: false,
  disableOnMedium: false,
  disableOnLarge: false,
  // [x, y] - x,y position from top left corner
  disabledPosition: null,
  // event hooks - function(element, x, y, event)
  onDrag: () => {},
  onDragStart: () => {},
  onDragEnd: () => {},
  onSizeChange: () => {}
};
const MEDIA = {
  small: window.matchMedia('(max-width: 767px)'),
  medium: window.matchMedia('(min-width: 768px) and (max-width: 1024px)'),
  large: window.matchMedia('(min-width: 1025px)')
};
const THROTTLE_TIME = 1000;
const isElement = obj => !!(obj && obj.nodeType === 1);

/**
 * Set element we want to attach events from options.handle or element if no
 * option provided
 * @param {object} options - object with options
 * @param {HTMLElement} element - element to position
 * @return {HTMLElement} element
 */
const handleEvent = (options, element) => options.handle && isElement(options.handle) ? options.handle : element;

/**
 * Get limit for x or y - getLimit(x, limit.x) or getLimit(y, limit.y)
 * @param {number} n - current x or y position
 * @param {Array} limit - [x y]
 * @return {number} x or y position
 */
const getLimit = (n, limit) => {
  if (Array.isArray(limit)) {
    if (n < limit[0]) return limit[0];
    if (n > limit[1]) return limit[1];
  }
  return n;
};

/**
 * Set limit initially so it can be returned later
 * @param {object} limit x and y [minimum position, maximum position]
 * @return {Function} x and y based on getLimit function
 */
const setLimit = limit => (x, y) => ({
  x: getLimit(x, limit.x),
  y: getLimit(y, limit.y)
});

/**
 * Set top and left position
 * @param {HTMLElement} element - element to position
 * @return {{top: number, left: number}} top and left position
 */
const setPosition = element => ({
  left: parseInt(element.style.left, 10) || element.offsetLeft,
  top: parseInt(element.style.top, 10) || element.offsetTop
});

/**
 * Get y and x position of the click or touch event
 * @param {DOMEvent} event - click for desktop and touch for tablet/mobile
 * @return {{x: number, y: number}} x and y position
 */
const getCursor = event => ({
  x: (event.targetTouches ? event.targetTouches[0] : event).clientX,
  y: (event.targetTouches ? event.targetTouches[0] : event).clientY
});

/**
 * Set x and y position for the cursor
 * @param {object} xy - x and y position
 * @return {undefined}
 */
function setCursor(xy) {
  this.cursor = xy;
}

/**
 * Use target element or use another Element based on filterTarget function or
 * element
 * @example In your module:
 *  const chatHeader = this.target.querySelector(SELECTORS.chatHeader);
 *  const filterTarget = chatHeader;
 *  or
 *  const filterTarget = target => (target === chatHeader);
 * @param {HTMLElement} element - event.target
 * @param {HTMLElement | function} filterTarget - defined in options
 * @return {*} if true, use target element
 */
const useTarget = (element, filterTarget) => {
  if (filterTarget instanceof Function) {
    return filterTarget(element);
  }
  if (isElement(filterTarget)) {
    return element === filterTarget;
  }
  return true;
};

/**
 * Add multiple event listeners
 * @param {HTMLElement} element - add to element
 * @param {object} events - object with events
 * @param {function | undefined} throttleEvent - throttle event function
 * @return {undefined}
 */
const addEventListeners = (element, events, throttleEvent) => {
  Object.keys(events).forEach(key => {
    if (throttleEvent) {
      element.addEventListener(key, throttleEvent, true);
    } else {
      element.addEventListener(key, events[key], true);
    }
  });
};

/**
 * Remove multiple event listeners
 * @param {HTMLElement} element - add to element
 * @param {object} events - object with events
 * @param {function | undefined} throttleEvent - throttle event function
 * @return {undefined}
 */
const removeEventListeners = (element, events, throttleEvent = undefined) => {
  Object.keys(events).forEach(key => {
    if (throttleEvent) {
      element.removeEventListener(key, throttleEvent, true);
    } else {
      element.removeEventListener(key, events[key], true);
    }
  });
};

/**
 * Check if dragging is disabled through options for current view port
 * @param {object} options - constructor options
 * @return {boolean} true if drag is disabled for current port view
 */
const isDisabled = options => MEDIA.small.matches && options.disableOnSmall || MEDIA.medium.matches && options.disableOnMedium || MEDIA.large.matches && options.disableOnLarge;

/**
 * Reposition draggable element on window resize and orientation change so
 * it always stays visible
 * @param {DOMEvent} event - start event
 * @return {undefined}
 */
function sizeChange(event) {
  this.options.onSizeChange(this.element, this.dragSettings.x, this.dragSettings.y, event);
  const {
    style
  } = this.element;
  const isDisabledForThisViewPort = isDisabled(this.options);
  const {
    disabledPosition
  } = this.options;
  if (isDisabledForThisViewPort && disabledPosition) {
    style.left = `${disabledPosition[0]}px`;
    style.top = `${disabledPosition[1]}px`;
  }
  this.limit = setLimit.call(this, this.options.limit);
  const currentPosition = setPosition(this.element);
  const newPosition = this.limit(currentPosition.left, currentPosition.top);
  if (newPosition.x !== currentPosition.left || newPosition.y !== currentPosition.top) {
    style.left = `${newPosition.x}px`;
    style.top = `${newPosition.y}px`;
    this.dragSettings.x = newPosition.x;
    this.dragSettings.y = newPosition.y;
  }
}

/**
 * Move element
 * @param {number} x position
 * @param {number} y position
 * @return {boolean} true if element was moved, false if not
 */
function move(x, y) {
  const {
    style
  } = this.element;
  const pos = this.limit(x, y);
  if (pos.x !== this.dragSettings.x || pos.y !== this.dragSettings.y) {
    this.dragSettings.x = pos.x;
    this.dragSettings.y = pos.y;
    style.left = `${pos.x}px`;
    style.top = `${pos.y}px`;
    return true;
  }
  return false;
}

/**
 * Initiate dragging (click on draggable element)
 * @param {DOMEvent} event - start event
 * @return {undefined}
 */
function start(event) {
  // drag only the intended target when not disabled
  if (this.targetElement && event.target !== this.targetElement || !useTarget(event.target, this.options.filterTarget) || !this.handle || isDisabled(this.options)) return;
  event.preventDefault();

  // set initial position
  setCursor.call(this, getCursor(event));
  Object.assign(this.position, setPosition(this.element));
  addEventListeners(document, this.handlers.move);
}

/**
 * Drag element
 * @param {DOMEvent} event - drag event
 * @return {undefined}
 */
function drag(event) {
  const newCursor = getCursor(event);
  const {
    threshold
  } = this.options;
  const x = newCursor.x - this.cursor.x + this.position.left;
  const y = newCursor.y - this.cursor.y + this.position.top;

  // check threshold
  if (!this.dragSettings.started && threshold && Math.abs(this.cursor.x - newCursor.x) < threshold && Math.abs(this.cursor.y - newCursor.y) < threshold) return;

  // save original position
  this.dragSettings.original = !this.dragSettings.original && {
    x,
    y
  };
  if (!this.dragSettings.started) {
    this.options.onDragStart(this.element, x, y, event);
    this.dragSettings.started = true;
  }
  if (move.call(this, x, y)) {
    this.options.onDrag(this.element, this.dragSettings.x, this.dragSettings.y, event);
  }
}

/**
 * Stop dragging
 * @param {DOMEvent} event - stop event
 * @return {undefined}
 */
function stop(event) {
  removeEventListeners(document, this.handlers.move);
  if (this.dragSettings.started) {
    this.options.onDragEnd(this.element, this.dragSettings.x, this.dragSettings.y, event);
  }
  this.dragSettings.started = false;
}

/**
 * Usage: new Draggable (element, options, targetElement)
 */
class Draggable {
  /**
   * Create new Draggable
   * @param {HTMLElement} element - draggable element
   * @param {object} options - object with options
   * @param {HTMLElement} targetElement - draggable target within element
   * @return {Draggable} object
   */
  constructor(element, options, targetElement = null) {
    if (!isElement(element)) {
      throw new TypeError('Draggable expects argument 1 to be an Element');
    }
    this.element = element;
    this.options = Object.assign({}, DEFAULT, options);
    this.targetElement = targetElement;
    this.initEvents();
    this.initProperties();
  }

  /**
   * Bind events
   * @return {undefined}
   */
  initEvents() {
    this.handle = handleEvent(this.options, this.element);
    const dragEvent = drag.bind(this);
    const stopEvent = stop.bind(this);
    const startEvent = start.bind(this);
    const sizeChangeEvent = sizeChange.bind(this);
    this.handlers = {
      start: {
        mousedown: startEvent,
        touchstart: startEvent
      },
      move: {
        mousemove: dragEvent,
        mouseup: stopEvent,
        touchmove: dragEvent,
        touchend: stopEvent
      },
      sizeChange: {
        resize: sizeChangeEvent,
        orientationchange: sizeChangeEvent
      }
    };
    this.throttleEvents = {
      sizeChangeEvent: _.throttle(sizeChangeEvent, THROTTLE_TIME)
    };
    addEventListeners(this.handle, this.handlers.start);
    addEventListeners(window, this.handlers.sizeChange, this.throttleEvents.sizeChangeEvent);
  }

  /**
   * Initialize properties - set position and limits
   * @return {undefined}
   */
  initProperties() {
    this.dragSettings = {
      started: false,
      x: 0,
      y: 0
    };
    const {
      style
    } = this.element;
    const dimensions = {
      width: this.options.dimensions && this.options.dimensions.width ? this.options.dimensions.width : this.element.offsetWidth,
      height: this.options.dimensions && this.options.dimensions.height ? this.options.dimensions.height : this.element.offsetHeight
    };
    if (this.options.isSetDimensions) {
      style.width = `${dimensions.width}px`;
      style.height = `${dimensions.height}px`;
    }
    this.position = {
      left: this.element.offsetLeft,
      top: this.element.offsetTop
    };
    this.limit = setLimit.call(this, this.options.limit);

    // make sure element is visible within limits even if
    // provided position is out of bound
    const pos = this.limit(this.options.position && this.options.position.left !== undefined ? this.options.position.left : this.position.left, this.options.position && this.options.position.top !== undefined ? this.options.position.top : this.position.top);

    // @TODO set initial position with transform3d
    const isDisabledForThisViewPort = isDisabled(this.options);
    if (this.options.isSetPosition && !isDisabledForThisViewPort) {
      style.left = `${pos.x}px`;
      style.top = `${pos.y}px`;
    }
    const {
      disabledPosition
    } = this.options;
    if (isDisabledForThisViewPort && disabledPosition) {
      style.left = `${disabledPosition[0]}px`;
      style.top = `${disabledPosition[1]}px`;
    }

    // set position
    Object.assign(this.dragSettings, {
      x: pos.x,
      y: pos.y
    });
  }

  /**
   * Set option
   * @param {string} property - option property
   * @param {string | object | function} value - option value
   * @return {undefined}
   */
  setOption(property, value) {
    this.options[property] = value;
  }

  /**
   * Get element's current position
   * @return {{x: number, y: number}} x and y position
   */
  getPosition() {
    return {
      x: this.dragSettings.x,
      y: this.dragSettings.y
    };
  }

  /**
   * Remove all event listeners
   * @return {undefined}
   */
  removeEvents() {
    removeEventListeners(this.handle, this.handlers.start);
    removeEventListeners(document, this.handlers.move);
    removeEventListeners(window, this.handlers.sizeChange, this.throttleEvents.sizeChangeEvent);
  }
}

export { Draggable as default };
